/* src/pages/admin/AdminDispatchersPage.tsx */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable, { TableColumn } from "react-data-table-component";
import AdminDashboardLayout from "../../components/admin/AdminDashboardLayout";
import DashboardCard from "../../components/common/DashboardCard";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { permissionCheckerService } from "../../services/permission.checker.service";
import {
    adminDispatcherService,
    AdminDispatcher,
    GetAdminDispatchersParams
} from "../../services/admin.dispatcher.service";
import { errorService } from "../../services/error.service";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faSearch,
    faCircleNotch,
    faTriangleExclamation,
    faAddressCard,
    faGlobe,
    faEye,
    faUserCheck,
    faUserSlash,
    faEdit,
    faTrash,
    faTruckFast,
    faPlus
} from "@fortawesome/free-solid-svg-icons";
import styles from "../../styles/pages/AdminDispatchersPage.module.css";
import btnStyles from "../../styles/components/Button.module.css";
import CreateDispatcherModal from "../../components/admin/CreateDispatcherModal";
import UpdateDispatcherModal from "../../components/admin/UpdateDispatcherModal";

const AdminDispatchersPage: React.FC = () => {
    const navigate = useNavigate();
    const { adminData } = useAdminAuth();

    // Check permissions
    useEffect(() => {
        if (!permissionCheckerService.checkPermission(adminData, "DISPATCHERS", "READ")) {
            navigate("/admin/dashboard", { replace: true });
        }
    }, [adminData, navigate]);

    const [dispatchers, setDispatchers] = useState<AdminDispatcher[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // Filter states
    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState<string>("");

    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedDispatcher, setSelectedDispatcher] = useState<AdminDispatcher | null>(null);

    const canRead = permissionCheckerService.checkPermission(adminData, "DISPATCHERS", "READ");
    const canCreate = permissionCheckerService.checkPermission(adminData, "DISPATCHERS", "CREATE");
    const canUpdate = permissionCheckerService.checkPermission(adminData, "DISPATCHERS", "UPDATE");
    const canDelete = permissionCheckerService.checkPermission(adminData, "DISPATCHERS", "DELETE");

    const fetchDispatchers = async (page: number, size: number) => {
        try {
            setLoading(true);
            setError(null);

            const params: GetAdminDispatchersParams = {
                page: page,
                pageSize: size,
                status: statusFilter === "" ? undefined : Number(statusFilter),
                SearchString: searchText || undefined,
            };

            const response = await adminDispatcherService.getDispatchers(params);

            if (response.statusCode === 200) {
                setDispatchers(response.data);
                setTotalRows(response.pagination.totalCount);
            } else {
                setError(errorService.getDisplayMessage(response));
            }
        } catch (err: any) {
            console.error("Error fetching admin dispatchers:", err);
            setError(errorService.getDisplayMessage(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDispatchers(1, perPage);
    }, [statusFilter]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        fetchDispatchers(page, perPage);
    };

    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setPerPage(newPerPage);
        fetchDispatchers(page, newPerPage);
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setCurrentPage(1);
        fetchDispatchers(1, perPage);
    };

    const handleToggleStatus = async (dispatcher: AdminDispatcher) => {
        const newStatus = dispatcher.status === 1 ? 0 : 1;
        const actionText = newStatus === 1 ? "activar" : "desactivar";

        const result = await Swal.fire({
            title: "¿Estás seguro?",
            text: `Esta acción procederá a ${actionText} al transportista ${dispatcher.firstName} ${dispatcher.lastName}.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: newStatus === 1 ? "#10b981" : "#f59e0b",
            cancelButtonColor: "#94a3b8",
            confirmButtonText: `Sí, ${actionText}`,
            cancelButtonText: "Cancelar",
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const response = await adminDispatcherService.changeDispatcherStatus({
                    id: dispatcher.id,
                    status: newStatus,
                });

                if (response.statusCode === 200) {
                    await Swal.fire({
                        icon: "success",
                        title: newStatus === 1 ? "Activado" : "Desactivado",
                        text: `El transportista ha sido ${newStatus === 1 ? "activado" : "desactivado"} con éxito.`,
                    });
                    fetchDispatchers(currentPage, perPage);
                } else {
                    errorService.showErrorAlert(response, "Error");
                }
            } catch (err) {
                console.error("Error updating dispatcher status:", err);
                errorService.showErrorAlert(err, "Error");
            } finally {
                setLoading(false);
            }
        }
    };

    const handleDelete = async (dispatcher: AdminDispatcher) => {
        const result = await Swal.fire({
            title: "¿Eliminar transportista?",
            text: `Esta acción eliminará de forma lógica al transportista ${dispatcher.firstName} ${dispatcher.lastName}.`,
            icon: "error",
            showCancelButton: true,
            confirmButtonColor: "#ef4444",
            cancelButtonColor: "#94a3b8",
            confirmButtonText: "Sí, eliminar",
            cancelButtonText: "Cancelar",
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const response = await adminDispatcherService.deleteDispatcher({
                    id: dispatcher.id,
                });

                if (response.statusCode === 200) {
                    await Swal.fire({
                        icon: "success",
                        title: "Eliminado",
                        text: "El transportista ha sido eliminado con éxito.",
                    });
                    fetchDispatchers(currentPage, perPage);
                } else {
                    errorService.showErrorAlert(response, "Error");
                }
            } catch (err) {
                console.error("Error deleting dispatcher:", err);
                errorService.showErrorAlert(err, "Error");
            } finally {
                setLoading(false);
            }
        }
    };

    const columns: TableColumn<AdminDispatcher>[] = [
        {
            name: "Código transportista",
            selector: (row) => row.code || "",
            sortable: true,
            minWidth: "140px",
        },
        {
            name: "Transportista",
            selector: (row) => `${row.firstName} ${row.lastName}`,
            sortable: true,
            minWidth: "180px",
            cell: (row) => (
                <div className="d-flex flex-column py-2">
                    <span className={styles.dispatcherName}>{row.firstName} {row.lastName}</span>
                </div>
            ),
        },
        {
            name: "Nro. documento",
            selector: (row) => row.dni,
            sortable: true,
            minWidth: "130px",
        },
        {
            name: "Teléfono",
            selector: (row) => row.phoneNumber || "",
            sortable: true,
            minWidth: "130px",
        },
        {
            name: "Email",
            selector: (row) => row.email || "",
            sortable: true,
            minWidth: "180px",
        },
        {
            name: "Fecha afiliación",
            selector: (row) => row.affiliationDate || "",
            sortable: true,
            minWidth: "140px",
            cell: (row) => (
                <span>
                    {row.affiliationDate
                        ? new Date(row.affiliationDate).toLocaleDateString("es-ES", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                          })
                        : "-"}
                </span>
            ),
        },
        {
            name: "País",
            selector: (row) => row.iso2Country || row.isO2Country || "",
            sortable: true,
            width: "80px",
        },
        {
            name: "Estado",
            selector: (row) => row.state || "",
            sortable: true,
            minWidth: "120px",
        },
        {
            name: "Cod.Postal",
            selector: (row) => row.zipCode || "",
            sortable: true,
            width: "100px",
        },
        {
            name: "Estatus",
            selector: (row) => row.status,
            sortable: true,
            cell: (row) => (
                <span className={`${styles.badge} ${row.status === 1 ? styles.statusActive : styles.statusInactive}`}>
                    {row.status === 1 ? "Activo" : "Inactivo"}
                </span>
            ),
            width: "110px",
        },
        {
            name: "Acciones",
            width: "180px",
            cell: (row) => (
                <div className="d-flex gap-2">
                    {canRead && (
                        <button
                            className={styles.detailButton}
                            onClick={() => window.open(`/admin/dispatcher/${row.id}`, "_blank")}
                            title="Ver detalles"
                        >
                            <FontAwesomeIcon icon={faEye} />
                        </button>
                    )}
                    {canUpdate && (
                        <button
                            className={styles.detailButton}
                            onClick={() => {
                                setSelectedDispatcher(row);
                                setShowEditModal(true);
                            }}
                            title="Editar transportista"
                        >
                            <FontAwesomeIcon icon={faEdit} />
                        </button>
                    )}
                    {canUpdate && (
                        <button
                            className={row.status === 1 ? styles.deactivateButton : styles.activateButton}
                            onClick={() => handleToggleStatus(row)}
                            title={row.status === 1 ? "Desactivar" : "Activar"}
                        >
                            <FontAwesomeIcon icon={row.status === 1 ? faUserSlash : faUserCheck} />
                        </button>
                    )}
                    {canDelete && (
                        <button
                            className={styles.deleteButton}
                            onClick={() => handleDelete(row)}
                            title="Eliminar"
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </button>
                    )}
                </div>
            ),
            right: true,
        }
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: "var(--elPaket-primary-color)",
                color: "white",
                minHeight: "48px",
            },
        },
        headCells: {
            style: {
                color: "var(--brand-black)",
                fontWeight: "700",
                fontSize: "12px",
                textTransform: "uppercase" as any,
                letterSpacing: "0.5px",
            },
        },
        rows: {
            style: {
                minHeight: "70px",
                "&:hover": {
                    backgroundColor: "#f8fafc !important",
                },
            },
        },
    };

    return (
        <AdminDashboardLayout>
            <div className={styles.adminDispatchersContainer}>
                <DashboardCard
                    icon={faTruckFast}
                    title="Gestión de Transportistas Afiliados"
                    description="Visualiza, afilia y gestiona el estado de los transportistas del sistema."
                    actions={
                        canCreate && (
                            <button
                                className={btnStyles.primary}
                                onClick={() => setShowCreateModal(true)}
                            >
                                <FontAwesomeIcon icon={faPlus} className="me-2" /> Registrar Transportista
                            </button>
                        )
                    }
                />

                <div className={styles.tableWrapper}>
                    <form className={styles.tableActions} onSubmit={handleSearch}>
                        <div className={styles.filterSection}>
                            <div className={styles.filterGroup}>
                                <label>Estado del Transportista</label>
                                <select
                                    className={styles.filterSelect}
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="">Todos</option>
                                    <option value="1">Activos</option>
                                    <option value="0">Inactivos</option>
                                </select>
                            </div>
                        </div>

                        <div className={styles.searchRow}>
                            <div className={styles.searchWrapper}>
                                <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
                                <input
                                    type="text"
                                    placeholder="Buscar por nombre, país, DNI..."
                                    className={styles.searchInput}
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                />
                            </div>
                            <button type="submit" className={btnStyles.primary}>
                                <FontAwesomeIcon icon={faSearch} /> Buscar
                            </button>
                        </div>
                    </form>

                    {error ? (
                        <div className={styles.emptyContainer}>
                            <FontAwesomeIcon icon={faTriangleExclamation} size="3x" style={{ color: "#ef4444", marginBottom: "16px" }} />
                            <h3>Error de Carga</h3>
                            <p>{error}</p>
                            <button className={btnStyles.primary} style={{ margin: "20px auto" }} onClick={() => fetchDispatchers(currentPage, perPage)}>
                                Reintentar
                            </button>
                        </div>
                    ) : dispatchers.length === 0 && !loading ? (
                        <div className={styles.emptyContainer}>
                            <FontAwesomeIcon icon={faTruckFast} size="3x" style={{ marginBottom: "16px", opacity: 0.5 }} />
                            <p>No se encontraron transportistas con los criterios de búsqueda.</p>
                        </div>
                    ) : (
                        <DataTable
                            columns={columns}
                            data={dispatchers}
                            pagination
                            paginationServer
                            paginationTotalRows={totalRows}
                            paginationDefaultPage={currentPage}
                            onChangeRowsPerPage={handlePerRowsChange}
                            onChangePage={handlePageChange}
                            progressPending={loading}
                            progressComponent={
                                <div className={styles.loadingContainer}>
                                    <FontAwesomeIcon icon={faCircleNotch} spin size="3x" className={styles.loadingIcon} />
                                    <p>Cargando transportistas...</p>
                                </div>
                            }
                            customStyles={customStyles}
                            responsive
                            highlightOnHover
                            noDataComponent={null}
                        />
                    )}
                </div>
            </div>

            <CreateDispatcherModal
                show={showCreateModal}
                onHide={() => setShowCreateModal(false)}
                onSuccess={() => fetchDispatchers(currentPage, perPage)}
            />

            <UpdateDispatcherModal
                show={showEditModal}
                onHide={() => {
                    setShowEditModal(false);
                    setSelectedDispatcher(null);
                }}
                onSuccess={() => fetchDispatchers(currentPage, perPage)}
                dispatcher={selectedDispatcher}
            />
        </AdminDashboardLayout>
    );
};

export default AdminDispatchersPage;
